import React, { useCallback, useEffect, useRef, useState } from "react";
import { getAuth } from "firebase/auth";
import { getStorage, ref, uploadString } from "firebase/storage";
import {
  DEFAULT_CANVAS_BACKGROUND,
  ICON_LIBRARY_ASSET_URL,
  ICON_LIBRARY_MAX_RESULTS,
  ICON_LIBRARY_SEARCH_URL,
} from "../../config/designStudioConfig";
import "./DesignStudio.scss";

const SvgIcon = ({ children, className = "", ...rest }) => (
  <svg
    className={`ds-icon ${className}`.trim()}
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
    {...rest}
  >
    {children}
  </svg>
);

const IconUndo = (props) => (
  <SvgIcon {...props}>
    <path d="M12 5V3.41a1 1 0 0 0-1.7-.7L4.3 8.7a1 1 0 0 0 0 1.4l6 6a1 1 0 0 0 1.7-.7V13h1a6 6 0 0 1 5.92 4.95l.05.45a1 1 0 0 0 2-.2 8 8 0 0 0-7.97-7.2H12V5Z" />
  </SvgIcon>
);

const IconRedo = (props) => (
  <SvgIcon {...props}>
    <path d="M12 5V3.41a1 1 0 0 1 1.7-.7l6 6a1 1 0 0 1 0 1.4l-6 6a1 1 0 0 1-1.7-.7V13h-1a6 6 0 0 0-5.92 4.95l-.05.45a1 1 0 1 1-2-.2 8 8 0 0 1 7.97-7.2H12V5Z" />
  </SvgIcon>
);

const IconBroom = (props) => (
  <SvgIcon {...props}>
    <path d="M20.44 3.56a1.5 1.5 0 0 0-2.12 0L13 8.88 11.12 7a1 1 0 0 0-1.41 0L8 8.71a1 1 0 0 0 0 1.41l1.88 1.88-6 6a1 1 0 0 0 .7 1.7H7l1.29 1.29a1 1 0 0 0 1.42 0l6-6 1.88 1.88a1 1 0 0 0 1.41 0L19 14.12a1 1 0 0 0 0-1.41L17.12 11l4.32-4.32a1.5 1.5 0 0 0 0-2.12ZM7.41 18H6l5-5 1 1-4.59 4Z" />
  </SvgIcon>
);

const IconSearch = (props) => (
  <SvgIcon {...props}>
    <path d="M15.5 14h-.79l-.28-.27a6.5 6.5 0 1 0-.71.71l.27.28v.79l5 5 1.5-1.5-5-5Zm-6 0a4.5 4.5 0 1 1 0-9 4.5 4.5 0 0 1 0 9Z" />
  </SvgIcon>
);

const IconSpinner = (props) => (
  <SvgIcon {...props}>
    <path d="M12 2a1 1 0 0 1 1 1v2a1 1 0 1 1-2 0V3a1 1 0 0 1 1-1Zm7.07 3.05a1 1 0 0 1 0 1.41l-1.42 1.42a1 1 0 0 1-1.41-1.41l1.41-1.42a1 1 0 0 1 1.42 0ZM21 11a1 1 0 0 1 0 2h-2a1 1 0 1 1 0-2h2Zm-3.95 7.07a1 1 0 0 1-1.41 0l-1.42-1.42a1 1 0 0 1 1.41-1.41l1.42 1.41a1 1 0 0 1 0 1.42ZM13 19v2a1 1 0 1 1-2 0v-2a1 1 0 1 1 2 0Zm-7.07-3.95a1 1 0 0 1 1.41 0l1.42 1.42a1 1 0 0 1-1.41 1.41l-1.42-1.41a1 1 0 0 1 0-1.42ZM5 11a1 1 0 1 1 0 2H3a1 1 0 0 1 0-2h2Zm3.95-7.07a1 1 0 0 1 0 1.41L7.54 6.76A1 1 0 0 1 6.13 5.34l1.42-1.42a1 1 0 0 1 1.41 0Z" />
  </SvgIcon>
);

const IconInfo = (props) => (
  <SvgIcon {...props}>
    <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2Zm0 3a1.25 1.25 0 1 1-1.25 1.25A1.25 1.25 0 0 1 12 5Zm2 12h-4v-2h1v-3h-1v-2h3v5h1Z" />
  </SvgIcon>
);

const IconSuccess = (props) => (
  <SvgIcon {...props}>
    <path d="M9.75 16.5 5.5 12.25l1.41-1.41L9.75 13.7l7.34-7.34 1.41 1.41Z" />
  </SvgIcon>
);

const IconWarning = (props) => (
  <SvgIcon {...props}>
    <path d="m12 3 9 16H3Zm0 3.46L6.53 17h10.94ZM11 11h2v4h-2Zm0 5h2v2h-2Z" />
  </SvgIcon>
);

const IconError = (props) => (
  <SvgIcon {...props}>
    <path d="m13.41 12 3.29-3.29-1.41-1.41L12 10.59 8.71 7.3 7.3 8.71 10.59 12 7.3 15.29l1.41 1.41L12 13.41l3.29 3.29 1.41-1.41Z" />
  </SvgIcon>
);

const IconDuplicate = (props) => (
  <SvgIcon {...props}>
    <path d="M7 5V3a1 1 0 0 1 1-1h11a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1h-2v4a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Zm1 2v10h8v-3H9a1 1 0 0 1-1-1V7Zm3 4h6V4H9v6h2Z" />
  </SvgIcon>
);

const IconBringForward = (props) => (
  <SvgIcon {...props}>
    <path d="M7 7h6v2H9v8h8v-4h2v5a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1Zm10-4a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1h-9a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h9Zm-8 2v7h7V5h-7Z" />
  </SvgIcon>
);

const IconSendBackward = (props) => (
  <SvgIcon {...props}>
    <path d="M17 17h-6v-2h4V7H7v4H5V6a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v10ZM7 21a1 1 0 0 1-1-1V11a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H7Zm1-8v7h7v-7H8Z" />
  </SvgIcon>
);

const IconDelete = (props) => (
  <SvgIcon {...props}>
    <path d="M9 3a1 1 0 0 0-1 1v1H4v2h1v11a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7h1V5h-4V4a1 1 0 0 0-1-1ZM9 7h10v11H7V7Zm2 3v7h2v-7Zm4 0v7h2v-7Zm-4-5h4V5h-4Z" />
  </SvgIcon>
);

const IconUpload = (props) => (
  <SvgIcon {...props}>
    <path d="M12 3 7 8h3v6h4V8h3Zm8 10h-2v5H6v-5H4v5a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2Z" />
  </SvgIcon>
);

const IconDownload = (props) => (
  <SvgIcon {...props}>
    <path d="M12 21 17 16h-3V10h-4v6H7Zm8-5h-2v-5H6v5H4v-5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2Z" />
  </SvgIcon>
);

const IconColorDrop = (props) => (
  <SvgIcon {...props}>
    <path d="M12 2 6.34 7.66a6 6 0 1 0 8.48 8.48A6 6 0 0 0 12 2Zm0 14a4 4 0 0 1-4-4 4 4 0 0 1 .93-2.57L12 6.34l3.07 3.09A4 4 0 0 1 12 16Z" />
  </SvgIcon>
);

const IconBrush = IconInfo;

const ALERT_ICONS = {
  info: IconInfo,
  success: IconSuccess,
  warning: IconWarning,
  error: IconError,
};

const SEARCH_MIN_LENGTH = 2;
const DEFAULT_ICON_QUERY = "star";
const DISPLAY_DPI = 72;
const EXPORT_DPI = 300;
const CANVAS_WIDTH_IN = 22;
const CANVAS_HEIGHT_IN = 22;
const ALPHA_THRESHOLD = 10;
const DISPLAY_CANVAS_WIDTH_PX = Math.round(CANVAS_WIDTH_IN * DISPLAY_DPI);
const DISPLAY_CANVAS_HEIGHT_PX = Math.round(CANVAS_HEIGHT_IN * DISPLAY_DPI);
const EXPORT_SCALE = EXPORT_DPI / DISPLAY_DPI;
const TARGET_OBJECT_INCHES = 3;
const TARGET_OBJECT_PX = TARGET_OBJECT_INCHES * DISPLAY_DPI;
const MIN_HANDLE_SIZE = 6;
const MIN_TOUCH_HANDLE_SIZE = 12;
const FONT_FAMILY_OPTIONS = [
  "Anek Latin",
  "Roboto",
  "Montserrat",
  "Poppins",
  "Inter",
  "Playfair Display",
  "Lato",
  "Nunito",
  "Merriweather",
  "Bebas Neue",
];

function clamp(value, min, max) {
  if (min > max) {
    const temp = min;
    min = max;
    max = temp;
  }
  return Math.min(Math.max(value, min), max);
}

// Sanitize a string for safe filenames and slugs
function slugifyName(input) {
  const base = (input || "").toString().trim().toLowerCase();
  if (!base) return "";
  // Replace non-alphanumeric with dashes, collapse repeats, and trim
  const slug = base
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug.slice(0, 64) || ""; // cap to 64 chars
}

const CRC32_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[n] = c >>> 0;
  }
  return table;
})();

function crc32(bytes) {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) {
    c = CRC32_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  }
  return (c ^ 0xffffffff) >>> 0;
}

function writeUint32BE(buffer, offset, value) {
  buffer[offset] = (value >>> 24) & 0xff;
  buffer[offset + 1] = (value >>> 16) & 0xff;
  buffer[offset + 2] = (value >>> 8) & 0xff;
  buffer[offset + 3] = value & 0xff;
}

function readUint32BE(buffer, offset) {
  return (
    (buffer[offset] << 24) |
    (buffer[offset + 1] << 16) |
    (buffer[offset + 2] << 8) |
    buffer[offset + 3]
  ) >>> 0;
}

function base64Decode(input) {
  if (typeof atob === "function") {
    return atob(input);
  }
  if (typeof Buffer !== "undefined") {
    return Buffer.from(input, "base64").toString("binary");
  }
  throw new Error("Base64 decode not supported in this environment");
}

function base64Encode(binary) {
  if (typeof btoa === "function") {
    return btoa(binary);
  }
  if (typeof Buffer !== "undefined") {
    return Buffer.from(binary, "binary").toString("base64");
  }
  throw new Error("Base64 encode not supported in this environment");
}

function ensurePngDpi(dataUrl, dpi = EXPORT_DPI) {
  if (typeof dataUrl !== "string" || !dataUrl.startsWith("data:image/png;base64,")) {
    return dataUrl;
  }

  const payload = dataUrl.split(",")[1];
  if (!payload) return dataUrl;

  const binary = base64Decode(payload);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }

  const targetPpu = Math.round(dpi / 0.0254);
  let offset = 8; // PNG signature
  let hasPhys = false;
  let outputBytes = bytes;

  while (offset + 8 < bytes.length) {
    const chunkLength = readUint32BE(bytes, offset);
    const chunkType = String.fromCharCode(
      bytes[offset + 4],
      bytes[offset + 5],
      bytes[offset + 6],
      bytes[offset + 7]
    );

    if (chunkType === "pHYs") {
      hasPhys = true;
      writeUint32BE(bytes, offset + 8, targetPpu);
      writeUint32BE(bytes, offset + 12, targetPpu);
      bytes[offset + 16] = 1; // unit: meter
      const crcInput = bytes.slice(offset + 4, offset + 4 + 13);
      const crc = crc32(crcInput);
      writeUint32BE(bytes, offset + 4 + 13, crc);
      break;
    }

    offset += 12 + chunkLength;
    if (chunkType === "IEND") break;
  }

  if (!hasPhys) {
    // Insert new pHYs chunk immediately after IHDR
    const ihdrLength = readUint32BE(bytes, 8);
    const ihdrTotal = 12 + ihdrLength;
    const insertPosition = 8 + ihdrTotal;

    const chunkData = new Uint8Array(9);
    writeUint32BE(chunkData, 0, targetPpu);
    writeUint32BE(chunkData, 4, targetPpu);
    chunkData[8] = 1;
    const chunkTypeBytes = new TextEncoder().encode("pHYs");
    const crcInput = new Uint8Array(4 + chunkData.length);
    crcInput.set(chunkTypeBytes, 0);
    crcInput.set(chunkData, 4);
    const crc = crc32(crcInput);

    const chunkLengthBytes = new Uint8Array(4);
    writeUint32BE(chunkLengthBytes, 0, chunkData.length);
    const crcBytes = new Uint8Array(4);
    writeUint32BE(crcBytes, 0, crc);

    const chunkTotalLength = 4 + 4 + chunkData.length + 4;
    const newBytes = new Uint8Array(bytes.length + chunkTotalLength);
    newBytes.set(bytes.slice(0, insertPosition), 0);
    let pos = insertPosition;
    newBytes.set(chunkLengthBytes, pos);
    pos += 4;
    newBytes.set(chunkTypeBytes, pos);
    pos += 4;
    newBytes.set(chunkData, pos);
    pos += chunkData.length;
    newBytes.set(crcBytes, pos);
    pos += 4;
    newBytes.set(bytes.slice(insertPosition), pos);
    outputBytes = newBytes;
    hasPhys = true;
  }

  let outBinary = "";
  for (let i = 0; i < outputBytes.length; i += 1) {
    outBinary += String.fromCharCode(outputBytes[i]);
  }
  const outBase64 = base64Encode(outBinary);
  return `data:image/png;base64,${outBase64}`;
}

function loadImageElement(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (err) => reject(err);
    img.src = src;
  });
}

async function trimTransparentDataUrl(dataUrl) {
  try {
    const img = await loadImageElement(dataUrl);
    const width = img.naturalWidth || img.width;
    const height = img.naturalHeight || img.height;
    if (!width || !height) return dataUrl;

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return dataUrl;
    ctx.drawImage(img, 0, 0);
    const { data } = ctx.getImageData(0, 0, width, height);

    let top = 0;
    let bottom = height - 1;
    let left = 0;
    let right = width - 1;
    let found = false;

    for (; top <= bottom; top++) {
      for (let x = 0; x < width; x++) {
        if (data[(top * width + x) * 4 + 3] > ALPHA_THRESHOLD) {
          found = true;
          break;
        }
      }
      if (found) break;
    }
    if (!found) return dataUrl;

    found = false;
    for (; bottom >= top; bottom--) {
      for (let x = 0; x < width; x++) {
        if (data[(bottom * width + x) * 4 + 3] > ALPHA_THRESHOLD) {
          found = true;
          break;
        }
      }
      if (found) break;
    }

    found = false;
    for (; left <= right; left++) {
      for (let y = top; y <= bottom; y++) {
        if (data[(y * width + left) * 4 + 3] > ALPHA_THRESHOLD) {
          found = true;
          break;
        }
      }
      if (found) break;
    }

    found = false;
    for (; right >= left; right--) {
      for (let y = top; y <= bottom; y++) {
        if (data[(y * width + right) * 4 + 3] > ALPHA_THRESHOLD) {
          found = true;
          break;
        }
      }
      if (found) break;
    }

    const cropWidth = Math.max(1, right - left + 1);
    const cropHeight = Math.max(1, bottom - top + 1);
    if (cropWidth === width && cropHeight === height) {
      return dataUrl;
    }

    const outCanvas = document.createElement("canvas");
    outCanvas.width = cropWidth;
    outCanvas.height = cropHeight;
    const outCtx = outCanvas.getContext("2d");
    if (!outCtx) return dataUrl;
    outCtx.drawImage(
      img,
      left,
      top,
      cropWidth,
      cropHeight,
      0,
      0,
      cropWidth,
      cropHeight
    );
    return outCanvas.toDataURL("image/png");
  } catch (error) {
    console.error("DesignStudio: trim failed", error);
    return dataUrl;
  }
}

function toHexColor(color, fabricNs, fallback = "#000000") {
  if (typeof color !== "string" || !color) {
    return fallback;
  }

  if (color.startsWith("#")) {
    if (color.length === 7) return color;
    if (color.length === 4) {
      return `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`;
    }
  }

  try {
    if (fabricNs?.Color) {
      const instance = new fabricNs.Color(color);
      if (typeof instance.toHex === "function") {
        return `#${instance.toHex()}`;
      }
      if (typeof instance.toHexa === "function") {
        const hexa = instance.toHexa();
        if (hexa) {
          return `#${hexa.slice(0, 6)}`;
        }
      }
    }
  } catch (error) {
    console.warn("DesignStudio: color normalization failed", error);
  }

  return fallback;
}

function scaleObjectToTarget(object, targetPx = TARGET_OBJECT_PX) {
  if (!object) return;
  // Prefer scaled width/height, fall back to base dimensions
  const scaledW =
    typeof object.getScaledWidth === "function"
      ? object.getScaledWidth()
      : (object.width || 0) * (object.scaleX || 1);
  const scaledH =
    typeof object.getScaledHeight === "function"
      ? object.getScaledHeight()
      : (object.height || 0) * (object.scaleY || 1);
  const currentMax = Math.max(scaledW || 0, scaledH || 0, 1);
  if (!Number.isFinite(currentMax) || currentMax <= 0) return;
  const factor = targetPx / currentMax;
  object.scaleX = (object.scaleX || 1) * factor;
  object.scaleY = (object.scaleY || 1) * factor;
  object.setCoords();
  adjustObjectControlSize(object);
}

function adjustObjectControlSize(object, scale = 1) {
  if (!object) return;
  const safeScale = Math.max(scale, 0.05);
  object.cornerSize = Math.max(MIN_HANDLE_SIZE, Math.round(14 / safeScale));
  object.touchCornerSize = Math.max(
    MIN_TOUCH_HANDLE_SIZE,
    Math.round(28 / safeScale)
  );
  object.borderScaleFactor = Math.max(1, Math.round(2 / safeScale));
  object.setCoords();
}

export default function DesignStudio() {
  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);
  const fileInputRef = useRef(null);
  const fabricRef = useRef(null);
  const fabricCanvasRef = useRef(null);
  const historyRef = useRef({ stack: [], index: -1, recording: false });
  const lastCanvasScaleRef = useRef(null);
  const viewportStateRef = useRef({ scale: 1, translateX: 0, translateY: 0 });
  const panningStateRef = useRef({
    active: false,
    pending: false,
    lastX: 0,
    lastY: 0,
  });

  const [canvas, setCanvas] = useState(null);
  const [initialised, setInitialised] = useState(false);
  const [selectedObject, setSelectedObject] = useState(null);
  const [, forceSelectionRender] = useState(0);
  const [backgroundColor, setBackgroundColor] = useState(
    DEFAULT_CANVAS_BACKGROUND
  );
  const [backgroundTransparent, setBackgroundTransparent] = useState(true);
  const [currentScale, setCurrentScale] = useState(1);
  const viewportOffsetRef = useRef({ x: 0, y: 0 });
  const [viewportOffset, setViewportOffset] = useState({ x: 0, y: 0 });
  const [viewportHeight, setViewportHeight] = useState(() => {
    if (typeof window !== "undefined") {
      const available = window.innerHeight - 240;
      return Math.max(320, Math.min(DISPLAY_CANVAS_HEIGHT_PX, available));
    }
    return Math.min(DISPLAY_CANVAS_HEIGHT_PX, 720);
  });

  const [iconQuery, setIconQuery] = useState(DEFAULT_ICON_QUERY);
  const [iconResults, setIconResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);

  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(null);
  const [saveError, setSaveError] = useState(null);

  const [downloading, setDownloading] = useState(false);

  const [designName, setDesignName] = useState("");

  const updateViewportOffset = useCallback((nextX, nextY) => {
    viewportOffsetRef.current = { x: nextX, y: nextY };
    setViewportOffset((prev) => {
      if (Math.abs(prev.x - nextX) < 0.5 && Math.abs(prev.y - nextY) < 0.5) {
        return prev;
      }
      return { x: nextX, y: nextY };
    });
  }, []);

  const applyViewportTransform = useCallback(
    (nextX, nextY, options = {}) => {
      const fabricCanvas = fabricCanvasRef.current;
      const { skipRender = false, skipState = false } = options;
      if (!fabricCanvas) {
        return { x: 0, y: 0 };
      }

      const lowerCanvas = fabricCanvas.lowerCanvasEl;
      const upperCanvas = fabricCanvas.upperCanvasEl;
      if (!lowerCanvas || !upperCanvas) {
        return {
          x: viewportStateRef.current.translateX || 0,
          y: viewportStateRef.current.translateY || 0,
        };
      }

      const viewportState = viewportStateRef.current;
      const currentX = viewportState.translateX || 0;
      const currentY = viewportState.translateY || 0;

      const wrapperWidth = wrapperRef.current?.clientWidth || 0;
      const wrapperHeight = wrapperRef.current?.clientHeight || 0;
      const minX = Math.min(0, wrapperWidth - DISPLAY_CANVAS_WIDTH_PX);
      const minY = Math.min(0, wrapperHeight - DISPLAY_CANVAS_HEIGHT_PX);

      const clampedX = clamp(Number.isFinite(nextX) ? nextX : 0, minX, 0);
      const clampedY = clamp(Number.isFinite(nextY) ? nextY : 0, minY, 0);

      const didChange =
        Math.abs(clampedX - currentX) > 0.01 ||
        Math.abs(clampedY - currentY) > 0.01;

      if (
        didChange &&
        typeof fabricCanvas.setViewportTransform === "function"
      ) {
        const transform = fabricCanvas.viewportTransform || [1, 0, 0, 1, 0, 0];
        if (
          Math.abs(transform[4] - clampedX) > 0.01 ||
          Math.abs(transform[5] - clampedY) > 0.01
        ) {
          fabricCanvas.setViewportTransform([1, 0, 0, 1, clampedX, clampedY]);
        }
      } else if (didChange && typeof fabricCanvas.setZoom === "function") {
        fabricCanvas.setZoom(1);
      }

      viewportState.translateX = clampedX;
      viewportState.translateY = clampedY;

      if (!skipState && didChange) {
        updateViewportOffset(clampedX, clampedY);
      }

      if (!skipRender) {
        if (typeof fabricCanvas.calcOffset === "function") {
          fabricCanvas.calcOffset();
        }
        fabricCanvas.requestRenderAll();
      }

      return { x: clampedX, y: clampedY };
    },
    [updateViewportOffset]
  );

  const ensureObjectVisible = useCallback(
    (object) => {
      if (!object || !canvas) return;
      const wrapperWidth = wrapperRef.current?.clientWidth || 0;
      const wrapperHeight = wrapperRef.current?.clientHeight || 0;
      if (!wrapperWidth || !wrapperHeight) return;

      const bounds = object.getBoundingRect(true, true);
      const currentX = viewportStateRef.current.translateX || 0;
      const currentY = viewportStateRef.current.translateY || 0;
      const visibleLeft = -currentX;
      const visibleTop = -currentY;
      const visibleRight = visibleLeft + wrapperWidth;
      const visibleBottom = visibleTop + wrapperHeight;

      let targetX = currentX;
      let targetY = currentY;

      if (bounds.left < visibleLeft) {
        targetX = -bounds.left;
      } else if (bounds.left + bounds.width > visibleRight) {
        targetX = wrapperWidth - (bounds.left + bounds.width);
      }

      if (bounds.top < visibleTop) {
        targetY = -bounds.top;
      } else if (bounds.top + bounds.height > visibleBottom) {
        targetY = wrapperHeight - (bounds.top + bounds.height);
      }

      applyViewportTransform(targetX, targetY);
    },
    [applyViewportTransform, canvas]
  );

  const canvasWidthInchesDisplay = CANVAS_WIDTH_IN.toFixed(2);
  const canvasHeightInchesDisplay = CANVAS_HEIGHT_IN.toFixed(2);

  const recordSnapshot = useCallback((fabricCanvas) => {
    if (!fabricCanvas) return;
    const history = historyRef.current;
    if (history.recording) return;
    try {
      history.recording = true;
      const json = fabricCanvas.toDatalessJSON();
      const entry = JSON.stringify(json);
      const nextStack = history.stack.slice(0, history.index + 1);
      nextStack.push(entry);
      history.stack = nextStack.slice(-30); // limit history length
      history.index = history.stack.length - 1;
    } catch (error) {
      console.error("DesignStudio: failed to snapshot", error);
    } finally {
      history.recording = false;
    }
  }, []);

  const restoreSnapshot = useCallback(
    (direction) => {
      const history = historyRef.current;
      if (!canvas) return;
      const nextIndex = history.index + direction;
      if (nextIndex < 0 || nextIndex >= history.stack.length) return;
      history.index = nextIndex;
      const entry = history.stack[nextIndex];
      try {
        const payload = JSON.parse(entry);
        history.recording = true;
        canvas.loadFromJSON(payload, () => {
          history.recording = false;
          canvas.renderAll();
          setSelectedObject(null);
          forceSelectionRender((v) => v + 1);
        });
      } catch (error) {
        history.recording = false;
        console.error("DesignStudio: failed to restore snapshot", error);
      }
    },
    [canvas]
  );

  useEffect(() => {
    let cancelled = false;
    let fabricCanvas;

    async function bootstrap() {
      try {
        const fabricModule = await import("fabric");
        if (cancelled) return;

        const fabricNs =
          fabricModule && typeof fabricModule === "object"
            ? fabricModule.fabric || fabricModule.default || fabricModule
            : fabricModule;

        if (!fabricNs || !fabricNs.Canvas) {
          throw new Error("Fabric.js runtime is unavailable");
        }

        fabricRef.current = fabricNs;
        fabricCanvas = new fabricNs.Canvas(canvasRef.current, {
          preserveObjectStacking: true,
          selection: true,
        });
        fabricCanvasRef.current = fabricCanvas;
        viewportStateRef.current = { scale: 1, translateX: 0, translateY: 0 };
        panningStateRef.current = {
          active: false,
          pending: false,
          lastX: 0,
          lastY: 0,
        };
        updateViewportOffset(0, 0);
        fabricCanvas.defaultCursor = "grab";
        if (typeof fabricCanvas.setBackgroundColor === "function") {
          fabricCanvas.setBackgroundColor(
            DEFAULT_CANVAS_BACKGROUND,
            fabricCanvas.renderAll.bind(fabricCanvas)
          );
        } else {
          fabricCanvas.backgroundColor = DEFAULT_CANVAS_BACKGROUND;
          fabricCanvas.requestRenderAll();
        }
        setCanvas(fabricCanvas);
        setInitialised(true);

        const handleSelection = (event) => {
          const obj = event?.selected?.[event.selected.length - 1] || null;
          setSelectedObject(obj);
          forceSelectionRender((v) => v + 1);
        };

        fabricCanvas.on("selection:created", handleSelection);
        fabricCanvas.on("selection:updated", handleSelection);
        fabricCanvas.on("selection:cleared", () => {
          setSelectedObject(null);
          forceSelectionRender((v) => v + 1);
        });
        fabricCanvas.on("object:modified", () => {
          forceSelectionRender((v) => v + 1);
          recordSnapshot(fabricCanvas);
        });

        const handlePanStart = (event) => {
          const pointerEvent = event?.e;
          if (!pointerEvent || pointerEvent.button !== 0) {
            return;
          }
          if (event?.target) {
            return;
          }
          const panState = panningStateRef.current;
          panState.pending = true;
          panState.active = false;
          panState.lastX = pointerEvent.clientX;
          panState.lastY = pointerEvent.clientY;
        };

        const handlePanMove = (event) => {
          const panState = panningStateRef.current;
          if (!panState.pending && !panState.active) {
            return;
          }
          const pointerEvent = event?.e;
          if (!pointerEvent) {
            return;
          }
          const dx = pointerEvent.clientX - panState.lastX;
          const dy = pointerEvent.clientY - panState.lastY;

          if (panState.pending) {
            const distance = Math.abs(dx) + Math.abs(dy);
            if (distance < 2) {
              return;
            }
            panState.pending = false;
            panState.active = true;
            fabricCanvas.selection = false;
            fabricCanvas.setCursor("grabbing");
          }

          if (!panState.active) {
            return;
          }

          const currentX = viewportStateRef.current.translateX || 0;
          const currentY = viewportStateRef.current.translateY || 0;
          applyViewportTransform(currentX + dx, currentY + dy);
          panState.lastX = pointerEvent.clientX;
          panState.lastY = pointerEvent.clientY;
        };

        const handlePanEnd = () => {
          const panState = panningStateRef.current;
          if (panState.active) {
            panState.active = false;
            fabricCanvas.selection = true;
            fabricCanvas.setCursor("grab");
          }
          panState.pending = false;
        };

        fabricCanvas.on("mouse:down", handlePanStart);
        fabricCanvas.on("mouse:move", handlePanMove);
        fabricCanvas.on("mouse:up", handlePanEnd);
        fabricCanvas.on("mouse:out", handlePanEnd);
      } catch (error) {
        console.error("DesignStudio: failed to initialise", error);
        setInitialised(false);
      }
    }

    bootstrap();

    return () => {
      cancelled = true;
      if (fabricCanvas) {
        fabricCanvas.dispose();
        if (fabricCanvasRef.current === fabricCanvas) {
          fabricCanvasRef.current = null;
        }
      }
    };
  }, [applyViewportTransform, recordSnapshot]);

  useEffect(() => {
    if (!canvas) return;

    let frame = null;
    lastCanvasScaleRef.current = null;

    const baseWidth = DISPLAY_CANVAS_WIDTH_PX;
    const baseHeight = DISPLAY_CANVAS_HEIGHT_PX;

    const applyScale = (_, wrapperEl) => {
      const clamped = 1;
      if (lastCanvasScaleRef.current !== clamped) {
        lastCanvasScaleRef.current = clamped;
        setCurrentScale(clamped);
      }

      const lowerCanvas = canvas.lowerCanvasEl;
      const upperCanvas = canvas.upperCanvasEl;
      if (!lowerCanvas || !upperCanvas) {
        return;
      }

      canvas.setDimensions(
        { width: baseWidth, height: baseHeight },
        { backstoreOnly: true }
      );

      canvas
        .getObjects()
        .forEach((obj) => adjustObjectControlSize(obj, clamped));

      const widthPx = `${baseWidth}px`;
      const heightPx = `${baseHeight}px`;

      viewportStateRef.current.scale = clamped;

      [lowerCanvas, upperCanvas, canvas.wrapperEl].forEach((el) => {
        if (!el) return;
        el.style.width = widthPx;
        el.style.height = heightPx;
        el.style.position = "absolute";
        el.style.top = "0px";
        el.style.left = "0px";
      });

      if (wrapperEl) {
        wrapperEl.style.minWidth = "0px";
        wrapperEl.style.minHeight = "0px";
      }

      applyViewportTransform(
        viewportStateRef.current.translateX || 0,
        viewportStateRef.current.translateY || 0,
        { skipRender: true, skipState: true }
      );

      if (typeof canvas.calcOffset === "function") {
        canvas.calcOffset();
      }
      canvas.requestRenderAll();
    };

    const scheduleSizeUpdate = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        const wrapperEl = wrapperRef.current;
        if (!wrapperEl) return;
        const bounds = wrapperEl.getBoundingClientRect();
        const widthScale = bounds.width ? bounds.width / baseWidth : 1;
        const heightScale = bounds.height
          ? bounds.height / baseHeight
          : Infinity;
        const nextScale = Math.min(
          Number.isFinite(widthScale) ? widthScale : 1,
          Number.isFinite(heightScale) ? heightScale : Infinity,
          1
        );
        if (!nextScale || !Number.isFinite(nextScale) || nextScale <= 0) {
          return;
        }
        applyScale(nextScale, wrapperEl);
      });
    };

    scheduleSizeUpdate();

    let observer;
    if (typeof ResizeObserver !== "undefined" && wrapperRef.current) {
      observer = new ResizeObserver(() => scheduleSizeUpdate());
      observer.observe(wrapperRef.current);
    }

    window.addEventListener("resize", scheduleSizeUpdate);

    return () => {
      if (observer) {
        observer.disconnect();
      }
      window.removeEventListener("resize", scheduleSizeUpdate);
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, [applyViewportTransform, canvas]);

  useEffect(() => {
    if (!canvas) return;
    if (historyRef.current.stack.length === 0) {
      recordSnapshot(canvas);
    }
  }, [canvas, recordSnapshot]);

  useEffect(() => {
    if (!canvas) return;
    canvas
      .getObjects()
      .forEach((obj) => adjustObjectControlSize(obj, currentScale));
    canvas.requestRenderAll();
  }, [canvas, currentScale]);

  const focusCanvas = useCallback(() => {
    if (!canvas) return;
    const active = canvas.getActiveObject();
    if (active && typeof canvas.bringToFront === "function") {
      canvas.bringToFront(active);
      canvas.renderAll();
    }
  }, [canvas]);

  const centerObject = useCallback(
    (object) => {
      if (!object || !canvas) return;
      const center = canvas.getCenter();
      object.set({
        left: center.left,
        top: center.top,
        originX: "center",
        originY: "center",
      });
      object.setCoords();
      adjustObjectControlSize(object, currentScale);
      if (!object.canvas) {
        canvas.add(object);
      }
      canvas.setActiveObject(object);
      canvas.requestRenderAll();
      focusCanvas();
      ensureObjectVisible(object);
    },
    [canvas, ensureObjectVisible, focusCanvas, currentScale]
  );

  const addShape = useCallback(
    (type) => {
      if (!canvas || !fabricRef.current) return;
      const fabric = fabricRef.current;
      let shape;
      const center = canvas.getCenter();
      const commonStyles = {
        left: center.left,
        top: center.top,
        originX: "center",
        originY: "center",
        fill: "#94C940",
        stroke: "#5A6872",
        strokeWidth: 2,
        cornerStyle: "circle",
        cornerColor: "#1F2937",
        transparentCorners: false,
      };

      switch (type) {
        case "rectangle":
          shape = new fabric.Rect({
            width: TARGET_OBJECT_PX,
            height: TARGET_OBJECT_PX * 0.6,
            rx: 12,
            ry: 12,
            ...commonStyles,
          });
          break;
        case "circle":
          shape = new fabric.Circle({
            radius: TARGET_OBJECT_PX / 2,
            ...commonStyles,
          });
          break;
        case "triangle":
          shape = new fabric.Triangle({
            width: TARGET_OBJECT_PX,
            height: TARGET_OBJECT_PX,
            ...commonStyles,
          });
          break;
        case "text":
          shape = new fabric.IText("Double-click to edit", {
            left: center.left,
            top: center.top,
            originX: "center",
            originY: "center",
            fontFamily: "Anek Latin",
            fontSize: 120,
            fill: "#1F2937",
            textAlign: "center",
          });
          break;
        default:
          return;
      }

      scaleObjectToTarget(shape);
      centerObject(shape);
    },
    [canvas, centerObject]
  );

  const handleIconSelect = useCallback(
    async (iconId) => {
      if (!canvas || !fabricRef.current) return;
      const fabric = fabricRef.current;
      try {
        const { objects, options } = await fabric.loadSVGFromURL(
          ICON_LIBRARY_ASSET_URL(iconId),
          undefined,
          { crossOrigin: "anonymous" }
        );
        const svg = fabric.util.groupSVGElements(objects, options);
        scaleObjectToTarget(svg);
        centerObject(svg);
      } catch (error) {
        console.error("DesignStudio: icon add failed", error);
      }
    },
    [canvas, centerObject]
  );

  const handleLocalUpload = useCallback(
    (event) => {
      if (!canvas || !fabricRef.current) return;
      const files = Array.from(event.target.files || []);
      if (!files.length) return;
      const fabric = fabricRef.current;

      const readFile = (file) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          if (file.type === "image/svg+xml") {
            reader.readAsText(file);
          } else {
            reader.readAsDataURL(file);
          }
        });

      (async () => {
        for (const file of files) {
          try {
            const result = await readFile(file);
            if (!result || !fabric) continue;

            if (file.type === "image/svg+xml") {
              const svgString = typeof result === "string" ? result : "";
              const { objects, options } = await fabric.loadSVGFromString(
                svgString,
                undefined,
                { crossOrigin: "anonymous" }
              );
              const svg = fabric.util.groupSVGElements(objects, options);
              scaleObjectToTarget(svg);
              centerObject(svg);
            } else {
              const dataUrl = typeof result === "string" ? result : "";
              const trimmedDataUrl = await trimTransparentDataUrl(dataUrl);
              const image = await fabric.Image.fromURL(trimmedDataUrl, {
                crossOrigin: "anonymous",
              });
              scaleObjectToTarget(image);
              centerObject(image);
            }
          } catch (error) {
            console.error("DesignStudio: upload failed", error);
          }
        }
        event.target.value = "";
      })();
    },
    [canvas, centerObject]
  );

  const performSearch = useCallback(async () => {
    if (!iconQuery || iconQuery.trim().length < SEARCH_MIN_LENGTH) {
      setSearchError(
        `Enter at least ${SEARCH_MIN_LENGTH} characters to search icons.`
      );
      setIconResults([]);
      return;
    }

    setIsSearching(true);
    setSearchError(null);
    try {
      const query = new URLSearchParams({
        query: iconQuery.trim(),
        limit: String(ICON_LIBRARY_MAX_RESULTS),
      }).toString();
      const response = await fetch(`${ICON_LIBRARY_SEARCH_URL}?${query}`);
      if (!response.ok) {
        throw new Error(`Icon search failed with status ${response.status}`);
      }
      const data = await response.json();
      setIconResults(Array.isArray(data?.icons) ? data.icons : []);
    } catch (error) {
      console.error("DesignStudio: icon search error", error);
      setSearchError("We couldn't reach the icon library. Try again shortly.");
      setIconResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [iconQuery]);

  const handleSearchSubmit = useCallback(
    (event) => {
      event.preventDefault();
      performSearch();
    },
    [performSearch]
  );

  useEffect(() => {
    performSearch();
  }, []); // intentionally run once on mount

  const deleteSelection = useCallback(() => {
    if (!canvas) return;
    const active = canvas.getActiveObjects();
    if (!active.length) return;
    active.forEach((obj) => canvas.remove(obj));
    canvas.discardActiveObject();
    canvas.requestRenderAll();
    setSelectedObject(null);
  }, [canvas]);

  const duplicateSelection = useCallback(() => {
    if (!canvas) return;
    const active = canvas.getActiveObject();
    if (!active) return;
    active.clone((cloned) => {
      cloned.left += 24;
      cloned.top += 24;
      canvas.add(cloned);
      canvas.setActiveObject(cloned);
      canvas.requestRenderAll();
    });
  }, [canvas]);

  const bringForward = useCallback(() => {
    if (!canvas) return;
    const active = canvas.getActiveObject();
    if (!active) return;
    if (typeof canvas.bringForward === "function") {
      canvas.bringForward(active);
    }
    canvas.requestRenderAll();
  }, [canvas]);

  const sendBackward = useCallback(() => {
    if (!canvas) return;
    const active = canvas.getActiveObject();
    if (!active) return;
    if (typeof canvas.sendBackwards === "function") {
      canvas.sendBackwards(active);
    }
    canvas.requestRenderAll();
  }, [canvas]);

  const clearCanvas = useCallback(() => {
    if (!canvas) return;
    canvas.getObjects().forEach((obj) => canvas.remove(obj));
    canvas.backgroundColor = backgroundTransparent ? null : backgroundColor;
    canvas.renderAll();
    setSelectedObject(null);
    recordSnapshot(canvas);
  }, [backgroundColor, backgroundTransparent, canvas, recordSnapshot]);

  const applyCanvasBackground = useCallback(() => {
    if (!canvas) return;
    const targetColor = backgroundTransparent ? null : backgroundColor;
    if (typeof canvas.setBackgroundColor === "function") {
      canvas.setBackgroundColor(targetColor, canvas.renderAll.bind(canvas));
    } else {
      canvas.backgroundColor = targetColor;
      canvas.requestRenderAll();
    }
  }, [backgroundColor, backgroundTransparent, canvas]);

  const updateBackgroundColor = useCallback((color) => {
    setBackgroundColor(color);
  }, []);

  useEffect(() => {
    applyCanvasBackground();
  }, [applyCanvasBackground]);

  useEffect(() => {
    const handleResize = () => {
      const available =
        typeof window !== "undefined"
          ? window.innerHeight - 240
          : DISPLAY_CANVAS_HEIGHT_PX;
      const clamped = Math.max(
        320,
        Math.min(DISPLAY_CANVAS_HEIGHT_PX, available)
      );
      setViewportHeight(clamped);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleBackgroundTransparencyToggle = useCallback((checked) => {
    setBackgroundTransparent(checked);
  }, []);

  const scaledWidth = Math.max(1, DISPLAY_CANVAS_WIDTH_PX * currentScale);
  const scaledHeight = Math.max(1, DISPLAY_CANVAS_HEIGHT_PX * currentScale);
  const inchPixelScaled = Math.max(6, DISPLAY_DPI * currentScale);
  const horizontalMarksCount = Math.floor(
    DISPLAY_CANVAS_WIDTH_PX / DISPLAY_DPI
  );
  const verticalMarksCount = Math.floor(DISPLAY_CANVAS_HEIGHT_PX / DISPLAY_DPI);
  const horizontalMarks = Array.from(
    { length: horizontalMarksCount + 1 },
    (_, i) => i
  );
  const verticalMarks = Array.from(
    { length: verticalMarksCount + 1 },
    (_, i) => i
  );
  const usedHorizontalPositions = new Set();
  const usedVerticalPositions = new Set();

  const selectedSummary = selectedObject
    ? {
        type: selectedObject.type,
        width: Math.round(selectedObject.getScaledWidth?.() || 0),
        height: Math.round(selectedObject.getScaledHeight?.() || 0),
        fill: selectedObject.fill || "",
        stroke: selectedObject.stroke || "",
        strokeWidth: selectedObject.strokeWidth || 0,
        fontSize: selectedObject.fontSize || 0,
        opacity: selectedObject.opacity ?? 1,
      }
    : null;

  const canvasSizeLabel = `${canvasWidthInchesDisplay}" × ${canvasHeightInchesDisplay}"`;
  const previewDpiLabel = `${DISPLAY_DPI} DPI preview`;
  const exportDpiLabel = `${EXPORT_DPI} DPI export`;
  const zoomPercent = Math.round(currentScale * 100);
  const maxCanvasCssWidth = DISPLAY_CANVAS_WIDTH_PX;
  const rulerOffsetX = viewportOffset.x;
  const rulerOffsetY = viewportOffset.y;
  const viewportHeightPx = `${Math.round(viewportHeight)}px`;

  const showFillControl = Boolean(selectedSummary?.fill);
  const fillInputValue = showFillControl
    ? toHexColor(selectedSummary.fill, fabricRef.current, "#000000")
    : "#000000";
  const showStrokeControl = selectedObject?.stroke != null;
  const strokeInputValue = showStrokeControl
    ? toHexColor(
        selectedSummary?.stroke || "#000000",
        fabricRef.current,
        "#000000"
      )
    : "#000000";
  const showStrokeWidthControl = selectedObject?.strokeWidth != null;
  const opacityValue = Number.isFinite(Number(selectedSummary?.opacity))
    ? Number(selectedSummary.opacity)
    : 1;
  const strokeWidthValue = Number.isFinite(Number(selectedSummary?.strokeWidth))
    ? Number(selectedSummary.strokeWidth)
    : 0;
  const isTextObject = selectedObject?.type === "i-text";
  const fontSizeValue = Number.isFinite(Number(selectedSummary?.fontSize))
    ? Number(selectedSummary.fontSize)
    : 72;
  const fontFamilyValue =
    (selectedObject?.fontFamily || "").trim() || FONT_FAMILY_OPTIONS[0];
  const selectionLabel = selectedSummary
    ? `${selectedSummary.type} · ${selectedSummary.width}px × ${selectedSummary.height}px`
    : "";

  const renderAlert = useCallback((type, message, options = {}) => {
    const AlertIcon = ALERT_ICONS[type] || ALERT_ICONS.info;
    const { onClose, className = "" } =
      typeof options === "object" && options !== null
        ? options
        : { onClose: options };
    return (
      <div className={`ds-alert ds-alert--${type} ${className}`.trim()}>
        <span className="ds-alert__icon">
          <AlertIcon />
        </span>
        <span className="ds-alert__message">{message}</span>
        {onClose && (
          <button
            type="button"
            className="ds-alert__close"
            onClick={onClose}
            aria-label="Dismiss message"
          >
            {"\u00d7"}
          </button>
        )}
      </div>
    );
  }, []);

  const updateSelectedObject = useCallback(
    (properties) => {
      if (!selectedObject || !canvas) return;
      selectedObject.set(properties);
      selectedObject.setCoords();
      canvas.requestRenderAll();
      forceSelectionRender((v) => v + 1);
    },
    [canvas, selectedObject]
  );

  const exportCanvasToDataUrl = useCallback(() => {
    if (!canvas) return null;

    const hasViewportTransform = Array.isArray(canvas.viewportTransform);
    const originalTransform = hasViewportTransform
      ? canvas.viewportTransform.slice()
      : null;
    const originalZoom =
      typeof canvas.getZoom === "function" ? canvas.getZoom() : 1;
    const originalBg = canvas.backgroundColor;
    const restoreBackground = () => {
      if (typeof canvas.setBackgroundColor === "function") {
        canvas.setBackgroundColor(originalBg, canvas.renderAll.bind(canvas));
      } else {
        canvas.backgroundColor = originalBg;
      }
    };

    if (canvas.backgroundColor) {
      if (typeof canvas.setBackgroundColor === "function") {
        canvas.setBackgroundColor(null, canvas.renderAll.bind(canvas));
      } else {
        canvas.backgroundColor = null;
      }
    }

    if (typeof canvas.setViewportTransform === "function") {
      canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
    } else if (typeof canvas.setZoom === "function") {
      canvas.setZoom(1);
    }

    canvas.renderAll();
    if (typeof canvas.calcOffset === "function") {
      canvas.calcOffset();
    }
    const rawDataUrl = canvas.toDataURL({
      format: "png",
      multiplier: EXPORT_SCALE,
    });
    const dataUrlPromise = trimTransparentDataUrl(rawDataUrl).then((trimmed) =>
      ensurePngDpi(trimmed, EXPORT_DPI)
    );

    if (
      typeof canvas.setViewportTransform === "function" &&
      originalTransform
    ) {
      canvas.setViewportTransform(originalTransform);
    } else if (typeof canvas.setZoom === "function") {
      canvas.setZoom(originalZoom);
    }

    canvas.renderAll();
    if (typeof canvas.calcOffset === "function") {
      canvas.calcOffset();
    }
    restoreBackground();
    return dataUrlPromise;
  }, [canvas]);

  const handleDownload = useCallback(async () => {
    if (!canvas) return;
    try {
      setDownloading(true);
      const dataUrl = await exportCanvasToDataUrl();
      if (!dataUrl) return;
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = `sodtf-design-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } finally {
      setDownloading(false);
    }
  }, [canvas, exportCanvasToDataUrl]);

  const handleSaveToLibrary = useCallback(async () => {
    if (!canvas) return;
    setSaveError(null);
    setSaveSuccess(null);
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      setSaveError("Please sign in to save your design to the builder.");
      return;
    }

    try {
      setSaving(true);
      const dataUrl = await exportCanvasToDataUrl();
      if (!dataUrl) {
        throw new Error("Unable to export design");
      }
      const storage = getStorage();
      const baseName = `design-${Date.now()}`;
      const userSlug = slugifyName(designName);
      const safeName = userSlug
        ? `${userSlug}-${Date.now()}.png`
        : `${baseName}.png`;
      const storageRef = ref(storage, `pictures/${user.uid}/${safeName}`);
      await uploadString(storageRef, dataUrl, "data_url", {
        customMetadata: { designName: designName || baseName },
      });
      setSaveSuccess(
        "Design saved! Open the DTF Sheet Builder and choose ‘Recently Uploaded’ to use it."
      );
    } catch (error) {
      console.error("DesignStudio: save failed", error);
      setSaveError("We couldn't save your design. Please try again.");
    } finally {
      setSaving(false);
    }
  }, [canvas, exportCanvasToDataUrl, designName]);

  const canUndo = historyRef.current.index > 0;
  const canRedo =
    historyRef.current.index < historyRef.current.stack.length - 1;

  return (
    <div className="ds-page">
      <div className="ds-container">
        <section className="ds-card ds-card--hero">
          <div className="ds-hero">
            <div className="ds-hero__copy">
              <h1 className="ds-heading">Design Studio</h1>
              <p className="ds-subheading">
                Build oversized print-ready artwork with precise controls. Drag
                the canvas, drop vectors, and fine-tune every layer before
                exporting at production quality.
              </p>
              <div className="ds-pill-row">
                <span className="ds-pill">{canvasSizeLabel}</span>
                <span className="ds-pill">{previewDpiLabel}</span>
                <span className="ds-pill">{exportDpiLabel}</span>
              </div>
            </div>
            <div className="ds-hero__actions">
              <button
                type="button"
                className="ds-button ds-button--toolbar"
                onClick={() => restoreSnapshot(-1)}
                disabled={!canUndo}
                title="Undo last action"
              >
                <IconUndo />
                <span>Undo</span>
              </button>
              <button
                type="button"
                className="ds-button ds-button--toolbar"
                onClick={() => restoreSnapshot(1)}
                disabled={!canRedo}
                title="Redo last action"
              >
                <IconRedo />
                <span>Redo</span>
              </button>
              <button
                type="button"
                className="ds-button ds-button--toolbar"
                onClick={clearCanvas}
                title="Clear canvas"
              >
                <IconBroom />
                <span>Clear</span>
              </button>
            </div>
          </div>
        </section>

        <div className="ds-layout">
          <div className="ds-column ds-column--sidebar">
            <section className="ds-card">
              <header className="ds-card__header">
                <h2 className="ds-card__title">Canvas Setup</h2>
                <p className="ds-card__subtitle">
                  Adjust the workspace backing and preview rulers to match your
                  project.
                </p>
              </header>
              <div className="ds-pill-row ds-pill-row--wrap">
                <span className="ds-pill">{canvasSizeLabel}</span>
                <span className="ds-pill">{`${zoomPercent}% zoom`}</span>
                <span className="ds-pill">{previewDpiLabel}</span>
              </div>
              <div className="ds-divider" />
              <label className="ds-toggle">
                <input
                  type="checkbox"
                  checked={backgroundTransparent}
                  onChange={(event) =>
                    handleBackgroundTransparencyToggle(event.target.checked)
                  }
                />
                <span className="ds-toggle__track">
                  <span className="ds-toggle__thumb" />
                </span>
                <span className="ds-toggle__label">Transparent background</span>
              </label>
              <div className="ds-color-picker">
                <IconColorDrop
                  className={`ds-icon ds-icon--muted ${
                    backgroundTransparent ? "ds-icon--disabled" : ""
                  }`}
                />
                <input
                  type="color"
                  className="ds-input ds-input--color"
                  value={backgroundColor}
                  disabled={backgroundTransparent}
                  onChange={(event) =>
                    updateBackgroundColor(event.target.value)
                  }
                />
              </div>
            </section>

            <section className="ds-card">
              <header className="ds-card__header">
                <h2 className="ds-card__title">Quick Elements</h2>
                <p className="ds-card__subtitle">
                  Drop in starter shapes or editable text to lay out your
                  composition.
                </p>
              </header>
              <div className="ds-button-grid">
                <button
                  type="button"
                  className="ds-button ds-button--ghost"
                  onClick={() => addShape("rectangle")}
                >
                  <span
                    className="ds-shape ds-shape--rect"
                    aria-hidden="true"
                  />
                  <span>Rectangle</span>
                </button>
                <button
                  type="button"
                  className="ds-button ds-button--ghost"
                  onClick={() => addShape("circle")}
                >
                  <span
                    className="ds-shape ds-shape--circle"
                    aria-hidden="true"
                  />
                  <span>Circle</span>
                </button>
                <button
                  type="button"
                  className="ds-button ds-button--ghost"
                  onClick={() => addShape("triangle")}
                >
                  <span
                    className="ds-shape ds-shape--triangle"
                    aria-hidden="true"
                  />
                  <span>Triangle</span>
                </button>
                <button
                  type="button"
                  className="ds-button ds-button--ghost"
                  onClick={() => addShape("text")}
                >
                  <span className="ds-shape ds-shape--text" aria-hidden="true">
                    Aa
                  </span>
                  <span>Text</span>
                </button>
              </div>
            </section>

            <section className="ds-card">
              <header className="ds-card__header">
                <h2 className="ds-card__title">Upload Artwork</h2>
                <p className="ds-card__subtitle">
                  Bring in PNG, JPEG, SVG, or WEBP assets and they’ll drop onto
                  the canvas at a balanced size.
                </p>
              </header>
              <button
                type="button"
                className="ds-button ds-button--ghost"
                onClick={() => fileInputRef.current?.click()}
              >
                <IconUpload />
                <span>Upload files</span>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                multiple
                className="ds-input ds-input--hidden"
                onChange={handleLocalUpload}
              />
            </section>

            <section className="ds-card ds-card--library">
              <header className="ds-card__header">
                <h2 className="ds-card__title">Icon Library</h2>
              </header>
              <form className="ds-search" onSubmit={handleSearchSubmit}>
                <div className="ds-search__field">
                  <IconSearch className="ds-icon ds-icon--muted" />
                  <input
                    type="search"
                    className="ds-input ds-input--search"
                    placeholder="Search icons or shapes"
                    value={iconQuery}
                    onChange={(event) => setIconQuery(event.target.value)}
                  />
                  <button
                    type="submit"
                    className="ds-button ds-button--small"
                    disabled={isSearching}
                  >
                    {isSearching ? "Searching" : "Search"}
                  </button>
                </div>
              </form>
              {searchError &&
                renderAlert("warning", searchError, {
                  onClose: () => setSearchError(null),
                })}
              <div className="ds-icon-results">
                {isSearching ? (
                  <div className="ds-loading">
                    <IconSpinner className="ds-spinner" />
                    <p className="ds-loading__label">Searching icons…</p>
                  </div>
                ) : iconResults.length === 0 ? (
                  <p className="ds-empty">
                    No icons yet. Try a different search term.
                  </p>
                ) : (
                  <div className="ds-icon-grid">
                    {iconResults.map((iconId) => (
                      <button
                        key={iconId}
                        type="button"
                        className="ds-icon-tile"
                        onClick={() => handleIconSelect(iconId)}
                        title={iconId}
                      >
                        <img
                          src={ICON_LIBRARY_ASSET_URL(iconId)}
                          alt={iconId}
                          loading="lazy"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {renderAlert(
                "info",
                "Click any icon to place it on the canvas. Use drag to reposition and the inspector to style.",
                {
                  className: "ds-alert--subtle",
                }
              )}
            </section>
          </div>

          <div className="ds-column ds-column--main">
            <section className="ds-card ds-card--canvas">
              <div className="ds-card__header ds-card__header--split">
                <div>
                  <h2 className="ds-card__title">Workspace Preview</h2>
                  <p className="ds-card__subtitle">
                    Drag the artboard with your mouse, use the mouse wheel or
                    trackpad pinch to zoom, and drop content anywhere within the
                    guides.
                  </p>
                </div>
                <div className="ds-pill-row">
                  <span className="ds-pill">{`${zoomPercent}% zoom`}</span>
                  <span className="ds-pill">Pan enabled</span>
                </div>
              </div>
              <div className="ds-divider" />
              <div className="ds-canvas-shell">
                <div className="ds-ruler ds-ruler--horizontal">
                  {horizontalMarks.map((mark) => {
                    const screenLeft = Math.round(
                      mark * inchPixelScaled + rulerOffsetX
                    );
                    if (
                      screenLeft < -inchPixelScaled ||
                      screenLeft > scaledWidth + inchPixelScaled ||
                      usedHorizontalPositions.has(screenLeft)
                    ) {
                      return null;
                    }
                    usedHorizontalPositions.add(screenLeft);
                    return (
                      <span
                        key={`hr-${mark}`}
                        className="ds-ruler__tick"
                        style={{ left: `${screenLeft}px` }}
                      >
                        <span className="ds-ruler__label">{`${mark}"`}</span>
                      </span>
                    );
                  })}
                </div>
                <div className="ds-ruler ds-ruler--vertical">
                  {verticalMarks.map((mark) => {
                    const screenTop = Math.round(
                      mark * inchPixelScaled + rulerOffsetY
                    );
                    if (
                      screenTop < -inchPixelScaled ||
                      screenTop > scaledHeight + inchPixelScaled ||
                      usedVerticalPositions.has(screenTop)
                    ) {
                      return null;
                    }
                    usedVerticalPositions.add(screenTop);
                    return (
                      <span
                        key={`vr-${mark}`}
                        className="ds-ruler__tick"
                        style={{ top: `${screenTop + 30}px` }}
                      >
                        <span className="ds-ruler__label">{`${mark}"`}</span>
                      </span>
                    );
                  })}
                </div>
                <div
                  className="ds-canvas-wrapper"
                  ref={wrapperRef}
                  style={{ height: viewportHeightPx }}
                >
                  <canvas
                    ref={canvasRef}
                    className="ds-canvas"
                    style={{
                      width: `${DISPLAY_CANVAS_WIDTH_PX}px`,
                      height: `${DISPLAY_CANVAS_HEIGHT_PX}px`,
                    }}
                  />
                </div>
              </div>
            </section>

            <section className="ds-card ds-card--inspector">
              <header className="ds-card__header">
                <h2 className="ds-card__title">Inspector</h2>
                <p className="ds-card__subtitle">
                  Select any object on the canvas to adjust color, opacity, and
                  layering.
                </p>
              </header>
              {selectedSummary ? (
                <div className="ds-inspector">
                  <span className="ds-pill ds-pill--muted">
                    {selectionLabel}
                  </span>

                  {showFillControl && (
                    <div className="ds-control">
                      <span className="ds-control__label">Fill</span>
                      <input
                        type="color"
                        className="ds-input ds-input--color"
                        value={fillInputValue}
                        onChange={(event) =>
                          updateSelectedObject({ fill: event.target.value })
                        }
                      />
                    </div>
                  )}

                  <div className="ds-control">
                    <span className="ds-control__label">Opacity</span>
                    <input
                      type="range"
                      min={0.1}
                      max={1}
                      step={0.05}
                      value={opacityValue}
                      onChange={(event) =>
                        updateSelectedObject({
                          opacity: Number(event.target.value),
                        })
                      }
                    />
                  </div>

                  {showStrokeControl && (
                    <div className="ds-control">
                      <span className="ds-control__label">Stroke</span>
                      <input
                        type="color"
                        className="ds-input ds-input--color"
                        value={strokeInputValue}
                        onChange={(event) =>
                          updateSelectedObject({ stroke: event.target.value })
                        }
                      />
                    </div>
                  )}

                  {showStrokeWidthControl && (
                    <div className="ds-control">
                      <span className="ds-control__label">Stroke width</span>
                      <input
                        type="range"
                        min={0}
                        max={20}
                        step={1}
                        value={strokeWidthValue}
                        onChange={(event) =>
                          updateSelectedObject({
                            strokeWidth: Number(event.target.value),
                          })
                        }
                      />
                    </div>
                  )}

                  {isTextObject && (
                    <>
                      <div className="ds-control">
                        <span className="ds-control__label">Font size</span>
                        <input
                          type="range"
                          min={12}
                          max={220}
                          step={2}
                          value={fontSizeValue}
                          onChange={(event) =>
                            updateSelectedObject({
                              fontSize: Number(event.target.value),
                            })
                          }
                        />
                      </div>
                      <label className="ds-field">
                        <span className="ds-field__label">Font family</span>
                        <select
                          className="ds-input ds-input--select"
                          value={fontFamilyValue}
                          onChange={(event) =>
                            updateSelectedObject({
                              fontFamily: event.target.value,
                            })
                          }
                        >
                          {FONT_FAMILY_OPTIONS.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="ds-field">
                        <span className="ds-field__label">Text</span>
                        <textarea
                          className="ds-textarea"
                          rows={3}
                          value={selectedObject.text || ""}
                          onChange={(event) =>
                            updateSelectedObject({ text: event.target.value })
                          }
                        />
                      </label>
                    </>
                  )}

                  <div className="ds-toolbar">
                    <button
                      type="button"
                      className="ds-button ds-button--icon"
                      onClick={duplicateSelection}
                      title="Duplicate"
                    >
                      <IconDuplicate />
                    </button>
                    <button
                      type="button"
                      className="ds-button ds-button--icon"
                      onClick={bringForward}
                      title="Bring forward"
                    >
                      <IconBringForward />
                    </button>
                    <button
                      type="button"
                      className="ds-button ds-button--icon"
                      onClick={sendBackward}
                      title="Send backward"
                    >
                      <IconSendBackward />
                    </button>
                    <button
                      type="button"
                      className="ds-button ds-button--icon ds-button--danger"
                      onClick={deleteSelection}
                      title="Delete"
                    >
                      <IconDelete />
                    </button>
                  </div>
                </div>
              ) : (
                renderAlert(
                  "info",
                  "Select an object on the canvas to reveal editing controls.",
                  {
                    className: "ds-alert--subtle",
                  }
                )
              )}
            </section>
          </div>
        </div>

        <section className="ds-card ds-card--footer">
          <div className="ds-footer__actions">
            <label className="ds-field ds-field--inline">
              <span className="ds-field__label">Design name</span>
              <input
                type="text"
                className="ds-input"
                placeholder="e.g., tag layout v1"
                value={designName}
                onChange={(event) => setDesignName(event.target.value)}
              />
            </label>
            <button
              type="button"
              className="ds-button ds-button--primary"
              onClick={handleSaveToLibrary}
              disabled={saving || !initialised}
            >
              <IconUpload />
              <span>{saving ? "Saving…" : "Save to My DTF Library"}</span>
            </button>
            <button
              type="button"
              className="ds-button ds-button--ghost"
              onClick={handleDownload}
              disabled={downloading || !initialised}
            >
              <IconDownload />
              <span>{downloading ? "Preparing…" : "Download PNG"}</span>
            </button>
            <span className="ds-pill ds-pill--muted">Exports at 300 DPI</span>
          </div>
          <div className="ds-footer__messages">
            {saveSuccess &&
              renderAlert("success", saveSuccess, {
                onClose: () => setSaveSuccess(null),
              })}
            {saveError &&
              renderAlert("error", saveError, {
                onClose: () => setSaveError(null),
              })}
            {!initialised &&
              renderAlert("info", "Loading the design tools…", {
                className: "ds-alert--subtle",
              })}
          </div>
        </section>
      </div>
    </div>
  );
}
